import * as React from 'react';
import { Headset } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSupportControllerTokenMutation } from '@/services/generated/api';
import { useSupportChat, type SupportChatState } from './use-support-chat';

function buttonLabel(isOpen: boolean, unread: number): string {
  if (isOpen) return 'Cerrar chat con soporte';
  if (unread === 1) return 'Chat con soporte (1 mensaje sin leer)';
  if (unread > 1) return `Chat con soporte (${unread} mensajes sin leer)`;
  return 'Chat con soporte';
}

/**
 * The header button that opens the support chat, wired to the widget and to
 * the backend's identity token. Mounted by `AppLayout`, so it only exists
 * with a session.
 */
export function SupportChatButton(): React.JSX.Element | null {
  const [requestToken] = useSupportControllerTokenMutation();
  const fetchToken = React.useCallback(
    () =>
      requestToken()
        .unwrap()
        .then(({ token }) => token)
        // 404 = the deployment has no Zendesk credentials; anything else is a
        // reason to stay anonymous rather than to break the chat.
        .catch(() => null),
    [requestToken],
  );
  const state = useSupportChat({ fetchToken });
  return <SupportChatButtonView state={state} />;
}

/**
 * The button itself. It renders nothing when the widget is off for this build
 * or failed to load (an ad blocker, no network): a button that does nothing is
 * worse than no button.
 */
export function SupportChatButtonView({
  state,
}: {
  state: SupportChatState;
}): React.JSX.Element | null {
  const { status, isOpen, unread, toggle } = state;

  if (status === 'unavailable') return null;

  return (
    <span className="relative inline-flex shrink-0">
      {/* A labelled button from `sm` up, not an icon alone. Beside «Cerrar
       * sesión», which shows its word, a bare headset was the one control on
       * the strip that had to be guessed — and a headset does not read as
       * "chat" to everyone. On a phone the strip has no room for the word, so
       * the icon stands alone there. The accessible name stays the stateful
       * one (unread count, open/closed); it contains the visible «Soporte», so
       * speech input that says what it sees still reaches it (WCAG 2.5.3). */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={buttonLabel(isOpen, unread)}
        title={buttonLabel(isOpen, unread)}
        aria-pressed={isOpen}
        disabled={status === 'loading'}
        onClick={toggle}
        className="shrink-0 text-fg-muted hover:text-fg max-sm:min-h-11"
      >
        <Headset aria-hidden="true" className="size-icon-sm" />
        <span className="hidden sm:inline">Soporte</span>
      </Button>
      {unread > 0 && !isOpen ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -right-1 -top-1 min-w-4 rounded-full bg-action px-1 text-center text-caption font-semibold text-fg-on-action"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </span>
  );
}
